import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { PharmacyLayout } from './PharmacyLayout';
import {
  usePharmacyLedger,
  useDeletePharmacyLedgerEntry,
  useBulkDeletePharmacyLedger,
  usePharmacyLedgerPurgePreview,
  useRetentionPolicies,
  useSaveRetentionPolicy,
} from '../../services/httpServices/queries';
import type { RetentionUnit } from '../../types/retention';
import { useDebounce } from '../../hooks/useDebounce';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { RetentionPurgeModal } from '../../components/ui/RetentionPurgeModal';
import { DrugDetailsModal } from '../../components/ui/DrugDetailsModal';
import { Loader2, AlertCircle, ScrollText, Search, X, Trash2, CalendarClock } from 'lucide-react';

const LIMIT = 20;

const typeFilters: Array<{ value: string; label: string }> = [
  { value: '', label: 'All' },
  { value: 'RECEIPT', label: 'Receipts' },
  { value: 'ISSUE', label: 'Issues' },
  { value: 'RETURN', label: 'Returns' },
  { value: 'ADJUSTMENT', label: 'Adjustments' },
  { value: 'EXPIRY_WRITE_OFF', label: 'Write-offs' },
];

const typeStyles: Record<string, string> = {
  RECEIPT: 'bg-success/10 text-success',
  ISSUE: 'bg-primary/10 text-primary',
  RETURN: 'bg-warning/10 text-warning',
  ADJUSTMENT: 'bg-background text-text-muted border border-border',
  EXPIRY_WRITE_OFF: 'bg-danger/10 text-danger',
};

export function PharmacyLedger() {
  const [type, setType] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [page, setPage] = useState(1);

  const filters = useMemo(
    () => ({
      ...(type ? { type } : {}),
      ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
    }),
    [type, debouncedSearch],
  );

  const { data, isLoading, error } = usePharmacyLedger(page, LIMIT, filters);
  const entries = useMemo(() => data?.data || [], [data]);
  const meta = data?.meta;

  const deleteEntry = useDeletePharmacyLedgerEntry();
  const bulkDelete = useBulkDeletePharmacyLedger();
  const saveRetentionPolicy = useSaveRetentionPolicy();
  const { data: retentionData } = useRetentionPolicies();

  // Row selection for bulk actions.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const [deleteTarget, setDeleteTarget] = useState<(typeof entries)[number] | null>(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  // Scheduled cleanup ("remove old entries") state.
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [retentionValue, setRetentionValue] = useState('730');
  const [retentionUnit, setRetentionUnit] = useState<RetentionUnit>('days');
  const parsedRetention = Number(retentionValue);
  const retentionValid = Number.isInteger(parsedRetention) && parsedRetention > 0;
  const {
    data: purgePreview,
    isFetching: purgePreviewLoading,
    isError: purgePreviewError,
  } = usePharmacyLedgerPurgePreview(retentionValid ? parsedRetention : 0, retentionUnit, purgeOpen);

  const [detailsDrugId, setDetailsDrugId] = useState<string | null>(null);
  const [detailsFallback, setDetailsFallback] = useState<{ name?: string; drugCode?: string } | null>(null);

  const openDetails = (entry: (typeof entries)[number]) => {
    const drugId = entry.drugId || entry.drug?.id;
    if (!drugId) return;
    setDetailsFallback({ name: entry.drug?.name, drugCode: entry.drug?.drugCode });
    setDetailsDrugId(drugId);
  };

  // Reset to the first page and clear selection when filters change.
  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [type, debouncedSearch]);

  // Drop selected ids that are no longer visible (e.g. after paging/filtering).
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(entries.map((entry) => entry.id));
      const next = new Set<string>();
      let changed = false;
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [entries]);

  const allSelected = entries.length > 0 && entries.every((entry) => selectedIds.has(entry.id));
  const someSelected = entries.some((entry) => selectedIds.has(entry.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const everySelected = entries.length > 0 && entries.every((entry) => prev.has(entry.id));
      return everySelected ? new Set() : new Set(entries.map((entry) => entry.id));
    });
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteEntry.mutateAsync(deleteTarget.id);
      toast.success('Ledger entry deleted');
      setSelectedIds((prev) => {
        if (!prev.has(deleteTarget.id)) return prev;
        const next = new Set(prev);
        next.delete(deleteTarget.id);
        return next;
      });
    } catch {
      toast.error('Failed to delete ledger entry');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleConfirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    try {
      await bulkDelete.mutateAsync(ids);
      setSelectedIds(new Set());
      toast.success(`Deleted ${ids.length} ledger entr${ids.length === 1 ? 'y' : 'ies'}`);
    } catch {
      toast.error('Failed to delete some ledger entries');
    } finally {
      setBulkConfirmOpen(false);
    }
  };

  const openPurge = () => {
    const policy = retentionData?.data?.policies?.find((p) => p.resource === 'pharmacy_ledger');
    setRetentionValue(String(policy?.value ?? 730));
    setRetentionUnit(policy?.unit ?? 'days');
    setPurgeOpen(true);
  };

  const handleSavePolicy = async (value: number, unit: RetentionUnit) => {
    try {
      await saveRetentionPolicy.mutateAsync({ resource: 'pharmacy_ledger', value, unit });
      setPurgeOpen(false);
      toast.success('Automatic ledger cleanup saved');
    } catch {
      toast.error('Failed to save cleanup settings');
    }
  };

  return (
    <PharmacyLayout
      title="Stock Ledger"
      description="Record of every pharmacy stock movement"
    >
      <div className="bg-surface rounded-xl border border-border p-4 shadow-sm space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {typeFilters.map((f) => (
            <button
              key={f.value}
              onClick={() => setType(f.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                type === f.value ? 'bg-primary text-white' : 'text-text-muted hover:text-text hover:bg-background'
              }`}
            >
              {f.label}
            </button>
          ))}
          <button
            type="button"
            onClick={openPurge}
            className="ml-auto px-3 py-1.5 text-sm font-medium text-danger border border-danger/30 hover:bg-danger/10 rounded-lg transition-colors flex items-center gap-1.5 whitespace-nowrap"
          >
            <CalendarClock size={16} />
            Remove old entries
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by drug, reference or notes..."
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            />
          </div>
          {search && (
            <button
              onClick={() => setSearch('')}
              className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      {someSelected && (
        <div className="flex flex-wrap items-center gap-2 bg-primary/5 border border-primary/20 rounded-xl p-3">
          <span className="text-sm text-text mr-2">
            <span className="font-medium">{selectedIds.size}</span> selected
          </span>
          <button
            onClick={() => setBulkConfirmOpen(true)}
            disabled={bulkDelete.isPending}
            className="px-3 py-1.5 rounded-lg bg-danger text-white text-xs font-medium hover:bg-danger/90 disabled:opacity-50 flex items-center gap-1.5"
          >
            {bulkDelete.isPending ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
            Delete
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            disabled={bulkDelete.isPending}
            className="px-3 py-1.5 rounded-lg text-text-muted hover:text-text border border-border text-xs font-medium disabled:opacity-50 flex items-center gap-1.5"
          >
            <X size={12} />
            Clear selection
          </button>
        </div>
      )}

      <div className="bg-surface rounded-xl border border-border overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left w-10">
                  <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={entries.length === 0}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                    title="Select all"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Drug</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Type</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Quantity</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Reference</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Notes</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-danger">
                    <AlertCircle size={18} className="inline mr-2" /> Failed to load ledger
                  </td>
                </tr>
              ) : entries.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-text-muted">
                    <ScrollText size={20} className="inline mr-2" /> No stock movements found.
                  </td>
                </tr>
              ) : (
                entries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-background/50 transition-colors">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(entry.id)}
                        onChange={() => toggleOne(entry.id)}
                        className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => openDetails(entry)}
                        className="font-medium text-text hover:text-primary hover:underline transition-colors text-left"
                        title="View drug details"
                      >
                        {entry.drug?.name || entry.drugId}
                      </button>
                      <div className="text-xs text-text-muted">{entry.batchId ? 'Batch linked' : ''}</div>
                    </td>
                    <td className="px-4 py-3 text-text-muted">
                      {new Date(entry.occurredAt || entry.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${typeStyles[entry.type] || ''}`}>
                        {entry.type.replace('_', ' ')}
                      </span>
                    </td>
                    <td
                      className={`px-4 py-3 text-right font-semibold ${
                        Number(entry.quantity) >= 0 ? 'text-success' : 'text-danger'
                      }`}
                    >
                      {Number(entry.quantity) > 0 ? '+' : ''}
                      {Number(entry.quantity)}
                    </td>
                    <td className="px-4 py-3 text-text-muted">{entry.referenceType || '—'}</td>
                    <td className="px-4 py-3 text-text-muted max-w-xs truncate">{entry.notes || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setDeleteTarget(entry)}
                        disabled={deleteEntry.isPending}
                        className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors disabled:opacity-50"
                        title="Delete entry"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {meta && (
          <Pagination
            page={meta.page}
            totalPages={meta.totalPages}
            total={meta.total}
            limit={meta.limit}
            onPageChange={setPage}
            itemName="ledger entries"
          />
        )}
      </div>

      {detailsDrugId && (
        <DrugDetailsModal
          drugId={detailsDrugId}
          fallback={detailsFallback}
          onClose={() => {
            setDetailsDrugId(null);
            setDetailsFallback(null);
          }}
        />
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete ledger entry"
        description={
          <>
            Delete this <strong>{deleteTarget?.type.replace('_', ' ')}</strong> entry for{' '}
            <strong>{deleteTarget?.drug?.name || deleteTarget?.drugId}</strong>? Stock levels are
            derived from the ledger and will change. This cannot be undone.
          </>
        }
        confirmLabel="Delete entry"
        loading={deleteEntry.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Delete ${selectedIds.size} ledger entr${selectedIds.size === 1 ? 'y' : 'ies'}?`}
        description={`This will remove ${selectedIds.size} selected ledger entr${
          selectedIds.size === 1 ? 'y' : 'ies'
        }. Stock levels are derived from the ledger and will change. This cannot be undone.`}
        confirmLabel={`Delete ${selectedIds.size} entr${selectedIds.size === 1 ? 'y' : 'ies'}`}
        loading={bulkDelete.isPending}
        onConfirm={handleConfirmBulkDelete}
        onCancel={() => setBulkConfirmOpen(false)}
      />

      <RetentionPurgeModal
        open={purgeOpen}
        loading={saveRetentionPolicy.isPending}
        itemSingular="ledger entry"
        itemPlural="ledger entries"
        value={retentionValue}
        unit={retentionUnit}
        onValueChange={setRetentionValue}
        onUnitChange={setRetentionUnit}
        preview={purgePreview?.data}
        previewLoading={purgePreviewLoading}
        previewError={purgePreviewError}
        nextRunAt={retentionData?.data?.schedule?.nextRunAt}
        timezone={retentionData?.data?.schedule?.timezone}
        onClose={() => setPurgeOpen(false)}
        onConfirm={handleSavePolicy}
      />
    </PharmacyLayout>
  );
}
